/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CalendarOutlined,
  DollarOutlined,
  IdcardOutlined,
  SyncOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { DateField, List, useModalForm, useTable } from '@refinedev/antd'
import {
  BaseRecord,
  CrudFilters,
  getDefaultFilter,
  IResourceComponentsProps
} from '@refinedev/core'
import { Badge, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { FC, Key, useCallback, useEffect, useMemo, useState } from 'react'

import { TableRowSelection } from 'antd/es/table/interface'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetListRequestMoney } from '../../api/requestMoney'
import { BalanceSelect, CurrencyBalance } from '../../components/balanceSelect/BalanceSelect'
import { cornerBadgeOnCell, NewTag } from '../components/newTag'
import { WorldcheckVerificationModal } from '../../components/modal/admin/worldCheckVerificationModal'
import PaymentStatus from '../../types/paymentStatus.type'
import { ModalType } from '../../types/ui'
import {
  errorNotification,
  paymentOrderEditQueryOptions,
} from '../../utils/edit.constants'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'
import { usePrevious } from '../../utils/hooks/usePrevious'
import {
  useWorldCheckVerification,
  VerificationStatus,
} from '../../utils/hooks/useWorldCheckVerification.hook'
import { disableCheckbox } from '../../utils/listColumnUtils'
import {
  paymentStatusColors,
  paymentStatusNames,
} from '../../utils/paymentStatus.utils'
import { ButtonListHeader } from '../components/buttonsList'
import { formatDate } from '../components/constants'
import { DownloadAttachmentsButton } from '../components/downloadAttachmentsButton'
import {
  PaymentOrderEditForm,
  PaymentSearchProps,
  Row,
} from '../components/interface'
import { StatusFilter } from '../components/StatusFilter'
import { TableForm } from '../crypto-withdrawal/tableForm'
import '../styles/index.css'
import { PaymentOrderEdit } from './editV2'

const ADMIN_COLUMN_KEYS = ['customer.fullName']

interface IRequestMoneySearchProps extends PaymentSearchProps {
  status: PaymentStatus
}

const start = 0
const end = 10

export const PaymentOrderList: FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate()
  const [editingId, setEditingId] = useState<number>()
  const prevEditingId = usePrevious(editingId)
  const [selectedIds, setSelectedIds] = useState<Key[]>([])
  const isAdminRole = useIsAdminRole()
  const [newViewedIds, setNewViewedIds] = useState<number[]>([])
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const current = queryParams.get('current')
  const pageSize = queryParams.get('pageSize')
  const editIdFromQuery = queryParams.get('editId')
  const [currentPage, setCurrentPage] = useState(current ? Number(current) : 1)
  const [searchStatus, setSearchStatus] = useState<any>()
  const [searchName, setSearchName] = useState<any>()
  const [searchDate, setSearchDate] = useState<any>()
  const [params, setParams] = useState({
    start: current ? (Number(current) - 1) * Number(pageSize) : start,
    end: pageSize ? Number(current) * Number(pageSize) : end,
  })

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
    formLoading: editFormLoading,
    id: modalEditingId,
    setId: setModalEditingId,
    queryResult: editQueryResult,
  } = useModalForm<PaymentOrderEditForm>({
    action: 'edit',
    onMutationSuccess: () => {
      editModalClose()
      setModalType('none')
      resetEditingId()
    },
    id: editingId,
    errorNotification,
    queryOptions: {
      ...paymentOrderEditQueryOptions,
      enabled: editingId !== undefined,
      cacheTime: 0,
    },
  })

  // const {
  //   modalProps: createCompanyModalProps,
  //   formProps: createCompanyFormProps,
  //   show: createCompanyModalShow,
  //   formLoading: createCompanyFormLoading,
  //   close: createCompanyModalClose,
  //   onFinish: createCompanyOnFinish,
  // } = useModalForm({
  //   action: "create",
  // });

  // const openCreateCompanyModal = () => {
  //   createCompanyModalShow()
  // }

  const onSearchPaymentOrders = (data: unknown) => {
    const params = data as IRequestMoneySearchProps
    const filters: CrudFilters = []
    const { searchValue, createdAt, status } = params
    setSearchStatus(PaymentStatus[status] ? status : undefined)
    setSearchName(searchValue)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    setSearchDate(
      createdAt && createdAt.length === 2
        ? {
          gte: dayjs(createdAt[0])
            .startOf('day')
            .toISOString(),
          lte: dayjs(createdAt[1])
            .endOf('day')
            .toISOString(),
          _timeZone: tz,
        }
        : undefined
    )
    setParams({
      start,
      end,
    })
    setCurrentPage(1)
    return filters
  }

  const { data, refetch: refetchListRequestMoney } = useGetListRequestMoney({
    ...params,
    status: searchStatus,
    name: searchName,
    gte: searchDate?.gte,
    lte: searchDate?.lte,
    timeZone: searchDate?._timeZone,
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setParams({
      start: (page - 1) * Number(pageSize) || start,
      end: page * Number(pageSize) || end,
    })
  }

  const prevEditingModalId = usePrevious(modalEditingId)

  const resetEditingId = useCallback(() => setEditingId(undefined), [])

  // Custom close handler to prevent API call when closing modal
  const handleEditModalClose = useCallback(() => {
    // Reset editingId first to disable query
    resetEditingId()
    setModalEditingId(undefined)
    setModalType('none')
    // Then close modal
    editModalClose()
  }, [resetEditingId, setModalEditingId, editModalClose])

  useEffect(() => {
    if (modalEditingId !== prevEditingModalId && modalEditingId !== undefined) {
      editQueryResult?.refetch()
      editModalShow(editingId)
    }
  }, [
    modalEditingId,
    editModalShow,
    editQueryResult,
    editingId,
    prevEditingModalId,
  ])

  useEffect(() => {
    if (editingId !== prevEditingId && editingId !== undefined) {
      setModalEditingId(editingId)
    }
  }, [editingId, prevEditingId, setModalEditingId])

  const {
    tableProps,
    searchFormProps,
    filters,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch: onSearchPaymentOrders,
  })

  useEffect(() => {
    setNewViewedIds([])
  }, [tableProps.dataSource])

  // Auto open edit modal when editId is in query params (after create)
  useEffect(() => {
    if (editIdFromQuery) {
      const id = Number(editIdFromQuery)
      if (id && !isNaN(id)) {
        setEditingId(id)
        setModalType('edit')
        // Remove editId from URL to clean up
        const newSearchParams = new URLSearchParams(location.search)
        newSearchParams.delete('editId')
        navigate(
          {
            pathname: location.pathname,
            search: newSearchParams.toString(),
          },
          { replace: true }
        )
      }
    }
  }, [editIdFromQuery, navigate, location])

  const createdAt = useMemo(() => {
    const start = getDefaultFilter('', filters, 'gte')
    const end = getDefaultFilter('', filters, 'lte')

    const startFrom = dayjs(start)
    const endAt = dayjs(end)

    if (start && end) {
      return [startFrom, endAt]
    }
    return undefined
  }, [filters])

  const {
    data: worldCheckVerification,
    fetch: fetchWorldCheckVerificationData,
  } = useWorldCheckVerification()

  const [modalType, setModalType] = useState<ModalType>('none')

  const handleCreateNew = () => {
    navigate('/payment-order/create')
  }

  const handleClickOnCell = useCallback(
    (record: BaseRecord) => {
      return {
        onClick: () => {
          if (
            [
              VerificationStatus.PENDING,
              VerificationStatus.REQUIRED,
              VerificationStatus.HIGH_RISK_FIR,
            ].some((status) => status === worldCheckVerification?.status) &&
            !isAdminRole
          ) {
            setModalType('worldCheck')
          } else {
            const id = Number(record.id)
            if (id) {
              setModalType('edit')
              setEditingId(id)
              if (record['rowNotViewed']) {
                setNewViewedIds((newViewedIds) => [...newViewedIds, id])
              }
            }
          }
        },
      }
    },
    [worldCheckVerification?.status, isAdminRole]
  )

  const columns = useMemo((): ColumnType<Row>[] => {
    const columns: ColumnType<Row>[] = [
      {
        dataIndex: 'id',
        title: (
          <span className={'column-header'}>
            <IdcardOutlined />
            &nbsp;ID
          </span>
        ),
        sorter: true,
        onCell: handleClickOnCell,
        render: (value: string) => (
          <span style={{ cursor: 'pointer' }}>{value}</span>
        ),
      },
      {
        dataIndex: ['customer', 'fullName'],
        title: isAdminRole && (
          <span className={'column-header'}>
            <UserOutlined />
            &nbsp;Customer
          </span>
        ),
        onCell: handleClickOnCell,
        sorter: true,
        key: 'customer.fullName',
      },
      {
        dataIndex: ['remitter', 'name'],
        title: (
          <span className={'column-header'}>
            <UserOutlined />
            &nbsp;Remitter
          </span>
        ),
        onCell: handleClickOnCell,
        sorter: true,
      },
      {
        dataIndex: ['serviceProvider', 'name'],
        title: (
          <span className={'column-header'}>
            <UserOutlined />
            &nbsp;Service provider
          </span>
        ),
        onCell: handleClickOnCell,
        sorter: true,
      },
      {
        dataIndex: 'status',
        title: (
          <span className={'column-header'}>
            <SyncOutlined />
            &nbsp;Status
          </span>
        ),
        onCell: handleClickOnCell,
        sorter: true,
        render: (value: string) => (
          <Badge
            color={paymentStatusColors[value]}
            text={paymentStatusNames[value]}
          />
        ),
      },
      {
        dataIndex: 'dateOfTransaction',
        title: (
          <span className={'column-header'}>
            <CalendarOutlined />
            &nbsp;Date
          </span>
        ),
        sorter: true,
        onCell: handleClickOnCell,
        render: (value: string) => (
          <DateField value={value} format={formatDate} />
        ),
      },
      {
        dataIndex: 'totalReceiveAmount',
        title: (
          <span className={'column-header'}>
            <DollarOutlined />
            &nbsp;Amount
          </span>
        ),
        onCell: handleClickOnCell,
        render: (value, record) => {
          return value
        },
      },
      {
        dataIndex: ['receiveCurrency', 'symbol'],
        title: (
          <span className={'column-header'}>
            <DollarOutlined />
            &nbsp;Currency
          </span>
        ),
        render: (value, record) => {
          if (record.svPayment) {
            return 'SV'
          }

          return value
        },
        onCell: handleClickOnCell,
      },
      {
        dataIndex: ['providerWallet', 'address'],
        title: (
          <span className={'column-header'}>
            <WalletOutlined />
            &nbsp;Wallet&nbsp;/&nbsp;Account
          </span>
        ),
        onCell: handleClickOnCell,
        render: (value: string | null, record: BaseRecord) =>
          record?.receiveUser && record?.receiveUser?.smartUID ? (
            <span>{record.receiveUser.smartUID}</span>
          ) : value ? (
            <span>{value}</span>
          ) : (record?.providerBankAccountNumber || record?.providerWalletAddress || record?.providerBankAccount?.accountNumber) ? (
            <span>{record.providerBankAccountNumber ?? record?.providerWalletAddress ?? record?.providerBankAccount?.accountNumber}</span>
          ) : (
            <span style={{ color: 'red' }}>N/A</span>
          ),
      },
      {
        dataIndex: 'createdAt',
        title: (
          <span className={'column-header'}>
            <CalendarOutlined />
            &nbsp;Created
          </span>
        ),
        render: (value: string) => (
          <DateField value={value} format={formatDate} />
        ),
      },
      {
        dataIndex: 'documentName',
        onCell: cornerBadgeOnCell(),
        render: (value: string, data) => (
          <>
            {value && (
              <DownloadAttachmentsButton entity={'payment_orders'} id={data.id} />
            )}
            {data['rowNotViewed'] && !newViewedIds.includes(data.id as number) && <NewTag />}
          </>
        ),
      },
    ]
    return columns.filter(
      ({ key }) => isAdminRole || !key || !ADMIN_COLUMN_KEYS.includes(`${key}`)
    )
  }, [handleClickOnCell, isAdminRole, newViewedIds])

  const statusFilterValue = useMemo<PaymentStatus | undefined>(
    () => getDefaultFilter('_status', filters),
    [filters]
  )

  const rowSelection = useMemo<TableRowSelection<Row>>(
    () => ({
      type: 'checkbox',
      selectedRowKeys: selectedIds,
      onChange: (selectedRowKeys) =>
        setSelectedIds(selectedRowKeys.slice(0, 5)),
      getCheckboxProps: disableCheckbox(selectedIds),
    }),
    [selectedIds]
  )

  return (
    <>
      <List
        title='Request Money'
        headerButtons={
          isAdminRole ? ButtonListHeader(handleCreateNew) : null
        }
      >
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
            createdAt,
          }}
          onSubmit={searchFormProps.form?.submit}
        />
        <StatusFilter
          {...searchFormProps}
          defaultValue={statusFilterValue}
          isRequestStatusName={true}
        />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={data?.data}
          rowSelection={rowSelection as any}
          rowClassName={(record) =>
            isAdminRole &&
              record['rowNotViewed'] &&
              !newViewedIds.includes(record.id as number)
              ? 'ant-table-highlighted-row'
              : ''
          }
          pagination={{
            current: currentPage,
            total: Number(data?.totalCount),
            pageSize: pageSize ? Number(pageSize) : 10,
            onChange: handlePageChange,
          }}
        />
      </List>
      {/* <PaymentOrderListCreate
        formProps={createCompanyFormProps}
        modalProps={createCompanyModalProps}
        formIsLoading={createCompanyFormLoading}
        formSubmit={createCompanyOnFinish}
        closeModal={createCompanyModalClose}
      /> */}
      {modalType === 'edit' && (
        <PaymentOrderEdit
          modalProps={editModalProps}
          formProps={editFormProps}
          queryResult={editQueryResult}
          id={modalEditingId}
          formIsLoading={editFormLoading}
          close={handleEditModalClose}
          resetEditingId={resetEditingId}
          refetchListRequestMoney={refetchListRequestMoney}
        />
      )}
      {modalType === 'worldCheck' && (
        <WorldcheckVerificationModal
          onClose={() => setModalType('none')}
          status={worldCheckVerification?.status}
          updateVerificationStatus={fetchWorldCheckVerificationData}
        />
      )}
    </>
  )
}
